import React from "react";
import { useDraggable } from "@dnd-kit/core";
import {
  ColumnHeightOutlined,
  FontSizeOutlined,
  LayoutOutlined,
  LinkOutlined,
  MinusOutlined,
  PictureOutlined,
  ProfileOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { BLOCK_KINDS, BlockType } from "../../blocks";
import { EMAIL_TEMPLATES, EmailTemplate } from "../../data";
import TemplateCard from "../../TemplateCard";

import PanelTitle from "../PanelTitle";

import styles from "./index.module.scss";

const ICONS: Record<BlockType, React.ReactNode> = {
  logo: <LayoutOutlined />,
  header: <LayoutOutlined />,
  title: <FontSizeOutlined />,
  text: <FontSizeOutlined />,
  image: <PictureOutlined />,
  imagetext: <ProfileOutlined />,
  button: <LinkOutlined />,
  divider: <MinusOutlined />,
  spacer: <ColumnHeightOutlined />,
  social: <ShareAltOutlined />,
};

/**
 * One kind of section, as something to drag into the letter.
 *
 * Dragging is the only way in: a section lands where it is dropped, and a click
 * that appended it to the end meant two ways to do one thing, one of which
 * ignored where the user was looking. The id is prefixed so the builder can
 * tell a new section from one already in the letter — dragging a block that
 * exists carries its own id.
 */
function KindButton({ type }: { type: BlockType }) {
  const { t } = useTranslation();
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({
    id: `new:${type}`,
    data: { type },
  });

  return (
    <button
      ref={setNodeRef}
      type="button"
      className={styles.kind}
      style={{ opacity: isDragging ? 0.5 : 1 }}
      {...attributes}
      {...listeners}
    >
      {ICONS[type]}
      {/* The panel names Heading and Paragraph apart — they are the same
          section, and the two buttons are what preset the style it opens at.
          Everything else falls back to the section's own name. */}
      {t(`pages.campaigns.blockKinds.${type}`, {
        defaultValue: t(`pages.campaigns.blockTypes.${type}`),
      })}
    </button>
  );
}

interface BlocksPanelProps {
  onApplyTemplate: (template: EmailTemplate) => void;
}

export default function BlocksPanel({ onApplyTemplate }: BlocksPanelProps) {
  const { t } = useTranslation();

  return (
    <div className={styles.panel}>
      <PanelTitle>{t("pages.campaigns.builder.blocks")}</PanelTitle>

      <div className={styles.grid}>
        {BLOCK_KINDS.map(({ type }) => (
          <KindButton key={type} type={type} />
        ))}
      </div>

      <PanelTitle>{t("pages.campaigns.view.templates")}</PanelTitle>

      <div className={styles.templates}>
        {EMAIL_TEMPLATES.map((template) => (
          <TemplateCard
            key={template.id}
            template={template}
            onPick={onApplyTemplate}
          />
        ))}
      </div>
    </div>
  );
}
